import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Grid, Box,
  Typography, Alert, Stack, InputAdornment, FormControl, InputLabel, Select, MenuItem,
  Card, CardContent, Divider
} from '@mui/material';
import { LocalShipping, AttachMoney, Inventory } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ingredientsService from '../../inventory/services/ingredientsService.js';
import { calculateUnitCost } from '../utils/ingredientUtils.js';

const IngredientReceivingDialog = ({ open, onClose, ingredient }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    quantity_received: '',
    unit_cost: '',
    supplier_name: '',
    invoice_number: '',
    received_date: new Date(),
    expiry_date: null,
    notes: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (ingredient && open) {
      setFormData({
        quantity_received: '',
        unit_cost: calculateUnitCost(ingredient.package_cost, ingredient.package_size).toFixed(4),
        supplier_name: ingredient.supplier_name || '',
        invoice_number: '',
        received_date: new Date(),
        expiry_date: null,
        notes: ''
      });
      setErrors({});
    }
  }, [ingredient, open]);

  const handleSubmit = async () => {
    const newErrors = {};
    
    if (!formData.quantity_received || parseFloat(formData.quantity_received) <= 0) {
      newErrors.quantity_received = t('ingredients.validation.qtyRequired');
    }
    
    if (!formData.unit_cost || parseFloat(formData.unit_cost) <= 0) {
      newErrors.unit_cost = t('ingredients.validation.unitCostRequired');
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    try {
      const currentStock = parseFloat(ingredient.current_stock || 0);
      const receivedQty = parseFloat(formData.quantity_received);
      const newStock = currentStock + receivedQty;

      await ingredientsService.updateIngredient(ingredient.ingredient_id, {
        current_stock: newStock,
        purchase_date: formData.received_date.toISOString().split('T')[0],
        expiry_date_on_purchase: formData.expiry_date ? formData.expiry_date.toISOString().split('T')[0] : null,
        supplier_name: formData.supplier_name || ingredient.supplier_name
      });

      onClose();
    } catch (error) {
      console.error('Error receiving stock:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!ingredient) return null;

  const totalValue = (parseFloat(formData.quantity_received || 0) * parseFloat(formData.unit_cost || 0)).toFixed(2);
  const newStockLevel = parseFloat(ingredient.current_stock || 0) + parseFloat(formData.quantity_received || 0);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
          color: 'white'
        }}>
          <LocalShipping />
          {t('ingredients.purchase.recordPurchase')} - {ingredient.name}
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            {/* Current Stock Info */}
            <Grid item xs={12} sx={{ mt: 4 }}>
              <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {t('ingredients.currentStock')} {t('common.information')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t('ingredients.currentStock')}
                      </Typography>
                      <Typography variant="h6">
                        {ingredient.current_stock} {ingredient.package_uom}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t('ingredients.unitCost')}
                      </Typography>
                      <Typography variant="h6">
                        ${calculateUnitCost(ingredient.package_cost, ingredient.package_size).toFixed(4)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Divider sx={{ width: '100%', my: 2 }} />

            {/* Receiving Information */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('ingredients.purchase.quantityPurchased')}
                type="number"
                value={formData.quantity_received}
                onChange={(e) => setFormData(prev => ({ ...prev, quantity_received: e.target.value }))}
                error={!!errors.quantity_received}
                helperText={errors.quantity_received || `${t('common.in')} ${ingredient.package_uom}`}
                required
                inputProps={{ min: 0, step: 0.001 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('ingredients.purchase.unitCost')}
                type="number"
                value={formData.unit_cost}
                onChange={(e) => setFormData(prev => ({ ...prev, unit_cost: e.target.value }))}
                error={!!errors.unit_cost}
                helperText={errors.unit_cost}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.0001 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DatePicker
                label={t('ingredients.purchase.purchaseDate')}
                value={formData.received_date}
                onChange={(value) => setFormData(prev => ({ ...prev, received_date: value }))}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DatePicker
                label={`${t('ingredients.expiryDate')} (${t('common.optional')})`}
                value={formData.expiry_date}
                onChange={(value) => setFormData(prev => ({ ...prev, expiry_date: value }))}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: t('ingredients.expiryDateHelp')
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('ingredients.supplier')}
                value={formData.supplier_name}
                onChange={(e) => setFormData(prev => ({ ...prev, supplier_name: e.target.value }))}
                placeholder={t('ingredients.supplierName')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('ingredients.purchase.invoice')}
                value={formData.invoice_number}
                onChange={(e) => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                placeholder={t('ingredients.purchase.invoiceNumber')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={`${t('ingredients.purchase.notes')} (${t('common.optional')})`}
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                multiline
                rows={2}
                placeholder={t('ingredients.purchase.notes')}
              />
            </Grid>

            {/* Summary */}
            {formData.quantity_received && formData.unit_cost && (
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    {t('ingredients.purchase.stockImpact')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>{t('ingredients.purchase.totalCost')}:</strong> ${totalValue}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>{t('inventory.setup.currentStock')}:</strong> {newStockLevel.toFixed(3)} {ingredient.package_uom}
                      </Typography>
                    </Grid>
                  </Grid>
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !formData.quantity_received || !formData.unit_cost}
            sx={{
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
              }
            }}
          >
            {loading ? t('ingredients.messages.recording') : t('ingredients.purchase.record')}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default IngredientReceivingDialog;